import type { Ref } from 'react';
import { createContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { RadioProps as AriaRadioProps, RadioRenderProps } from 'react-aria-components/RadioGroup';
import { Radio as AriaRadio } from 'react-aria-components/RadioGroup';
import type { ContextValue } from 'react-aria-components/slots';
import { cva } from 'class-variance-authority';

import { useLPContextProps } from './utils';

import styles from './styles/Radio.module.css';

const radioStyles = cva(styles.radio);
const radioIconStyles = cva(styles.circle);

interface RadioProps extends AriaRadioProps {
	ref?: Ref<HTMLLabelElement>;
}

const RadioContext = createContext<ContextValue<RadioProps, HTMLLabelElement>>(null);

const RadioIcon = ({ isSelected }: Partial<RadioRenderProps>) => (
	<div className={radioIconStyles()} data-lp-indicator="radio">
		{isSelected ? (
			<svg aria-hidden="true" className={styles.icon} viewBox="0 0 16 16">
				<path fillRule="evenodd" clipPath="evenodd" d="M6.852 5.228a3 3 0 1 1 2.296 5.544 3 3 0 0 1-2.296-5.544Z" />
			</svg>
		) : null}
	</div>
);

/**
 * A radio represents an individual option within a radio group.
 *
 * https://react-spectrum.adobe.com/react-aria/RadioGroup.html
 */
const Radio = ({ ref, ...props }: RadioProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, RadioContext);
	return (
		<AriaRadio
			{...mergedProps}
			ref={mergedRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				radioStyles({ ...renderProps, className }),
			)}
		>
			{composeRenderProps(mergedProps.children, (children, { isSelected }) => (
				<>
					<RadioIcon isSelected={isSelected} />
					{children}
				</>
			))}
		</AriaRadio>
	);
};

export { Radio, RadioContext, RadioIcon, radioIconStyles, radioStyles };
export type { RadioProps };
